/**
 * Элемент списка пользователя в поиске (без Card обёртки)
 * Используется внутри общего Card контейнера
 */

import { memo, type ReactNode } from 'react';
import { UserAvatar } from '@/components';
import { sanitizeUserInput, formatUserName } from '@/lib';
import type { UserProfile } from '../model';

interface UserSearchListItemProps {
  /** Данные пользователя */
  user: UserProfile;
  /** Слот для кнопки действия */
  actionSlot?: ReactNode;
  /** Callback при клике на карточку */
  onClick?: () => void;
  /** Callback для перехода на профиль пользователя */
  onUserClick?: (userId: string) => void;
  /** Функция перевода */
  t: (key: string) => string;
}

/**
 * Компонент элемента списка пользователя в поиске
 * Без обёртки Card - используется внутри общего списка
 */
export const UserSearchListItem = memo(function UserSearchListItem({ 
  user, 
  actionSlot, 
  onClick,
  onUserClick,
  t 
}: UserSearchListItemProps): JSX.Element {
  // Sanitize UGC для защиты от XSS
  const safeDisplayName = sanitizeUserInput(user.displayName);
  const safeLastName = sanitizeUserInput(user.lastName);
  const safeUsername = sanitizeUserInput(user.username);
  
  // Сокращённый формат: "Имя Ф." — для компактных строк
  const displayName = formatUserName(safeDisplayName, safeLastName, { short: true }) || safeUsername || t('common:noResults');
  
  // Определяем подзаголовок: username если есть display_name
  const subtitle = safeDisplayName && safeUsername ? `@${safeUsername}` : undefined;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (onUserClick) {
      onUserClick(user.id);
    }
  };

  return (
    <div className="flex items-center">
      <button
        type="button"
        onClick={handleClick}
        className={`flex items-center flex-1 min-w-0 text-left ${onClick || onUserClick ? 'active:scale-[0.98] transition-transform' : ''}`}
      >
        <UserAvatar
          userId={user.id}
          avatarUrl={user.avatarUrl}
          displayName={user.displayName}
          lastName={user.lastName}
          username={user.username}
          size="default"
          className="flex-shrink-0"
        />
        
        <div className="flex-1 min-w-0 ml-3.5">
          <p className="text-base text-foreground truncate">
            {displayName}
          </p>
          {subtitle && (
            <p className="text-sm text-muted-foreground font-normal truncate">
              {subtitle}
            </p>
          )}
        </div>
      </button>
      
      {actionSlot && (
        <div className="flex-shrink-0">
          {actionSlot}
        </div>
      )}
    </div>
  );
});